<template>
    <div class="content__page" :class="{ 'disabled': appState === 'duplicate_project_loading' }">
        <div>
            <template v-if="pageLoading">
                <PageSpinner />
            </template>

            <template v-else-if="pageLoadingErr">
                <EmptyPage state="error" @action="handleFetchProject" />
            </template>

            <template v-else>
                <div v-if="currentProject.status !== 'completed' && deadlineState.isDue" class="w-100 mb--25">
                    <Alert :type="deadlineState.color" classes="text--sm">
                        <template #text>
                            <p class="text--sm">
                                This project is <strong>{{ deadlineState.copy }}</strong>. Review your progress and complete project to stay on track.
                            </p>
                        </template>
                    </Alert>
                </div>

                <div class="project__header__wrap">
                    <div class="flex-2 pr--16">
                        <div class="mb--15">
                            <div class="flex align-items-center w--75">
                                <FormInput 
                                    v-model="currentProject.title"
                                    border="none" 
                                    wrapper-class="w-100 pl--0"
                                    input-type="debounced"
                                    class="text--semi--bold text-gray-600 text--xl pl--0"
                                    no-margin-bottom
                                    @input="updateProjectTitle"
                                />
                            </div>

                            <div class="col-12 cursor-text w--fit" @click="openEditModal('description')">
                                <template v-if="currentProject.description">
                                    <p class="text-gray-600 text--sm">
                                        {{ currentProject.description }}
                                    </p>
                                </template>
                                <template v-else>
                                    <p class="text-gray-300 text--sm">
                                        Description
                                    </p>
                                </template>
                            </div>
                        </div>

                        <div class="flex align-items-center justify-content-between">
                            <div class="project__item project__item__flex mb--0">
                                <span class="project__item__title text--normal mt--0 text--xs">
                                    Created on: {{ $format_date(currentProject.createdAt) }}
                                </span>
                                <span class="ml--10 mr--10 project__item__title text--normal"> | </span>
                                        
                                <div :class="{ 'disabled': appState === 'update_status_loading' }">
                                    <div class="dropdown">
                                        <div id="statusDropdown" class="pointer" data-bs-toggle="dropdown" aria-expanded="false">
                                            <div class="project__item__title flex align-items-center text--capitalize text--xs">
                                                <Status type="project" :value="currentProject.status || ''" />
                                                <IconSvg name="chevron-down" width="12px" />
                                            </div>
                                        </div>

                                        <List id="status">
                                            <ListItem 
                                                v-for="status in projectFilterStatuses" 
                                                :key="status"
                                                flex 
                                                pointer
                                                @click="handleStatusUpdate(status)"
                                            >
                                                <span class="status__circle" :class="[`${projectStatusMap[status]?.tag}__text`]" />
                                                <span class="text--xs text--capitalize text-gray-800">{{ status }}</span>
                                            </ListItem>
                                        </List>
                                    </div>
                                </div>

                                <span class="ml--10 mr--10 project__item__title text--normal"> | </span>
    
                                <div :class="{ 'disabled': appState === 'update_priority_loading' }">
                                    <div class="dropdown">
                                        <div id="priorityDropDown" class="pointer" data-bs-toggle="dropdown" aria-expanded="false">
                                            <div class="project__item__title flex align-items-center text--capitalize text--xs">
                                                <span class="box-icon mr-5" :class="[currentProject.priority]" />
                                                <span> {{ currentProject.priority || "-" }} priority</span>
                                                <IconSvg name="chevron-down" width="12px" />
                                            </div>
                                        </div>

                                        <List id="priority">
                                            <ListItem 
                                                v-for="priority in priorities" 
                                                :key="priority"
                                                flex 
                                                pointer
                                                @click="updatePriority(priority)"
                                            >
                                                <span class="box-icon mr--5" :class="[priority]" />
                                                <span class="text--xs text--capitalize text-gray-800">{{ priority }}</span>
                                            </ListItem>
                                        </List>
                                    </div>
                                </div>
                            </div>

                            <div class="flex-1 project__header__status justify-content-end w-100">
                                <BaseButton
                                    variant="outline" 
                                    icon-name="task" 
                                    icon-width="16px"
                                    variant-style="secondary" 
                                    class="mr--5" 
                                    label="View tasks" 
                                    prefix-icon
                                    @submit="goToTasks"
                                />
        
                                <div class="dropdown">
                                    <div id="invoiceDetailsDropDown" class="invoice__details__menu pointer" data-bs-toggle="dropdown" aria-expanded="false">
                                        <IconSvg name="dots-horizontal-rounded" />
                                    </div>
        
                                    <List id="invoiceDetails" style="min-width: fit-content;">
                                        <FormLabel title="Project actions" label-type="sm_mono" class="pl--10 dropdown-item-label text--medium" />
        
                                        <ListItem v-if="currentProject.status === 'completed'" flex pointer disabled>
                                            <div>
                                                <div class="flex align-items-center">
                                                    <IconSvg name="share" />
                                                    <span class="ml--10 text--sm text-gray-800">
                                                        Share project
                                                    </span>
                                                </div>
                                                <div class="flex justify-content-center">
                                                    <span class="tag bold text--xxs tag--grey mt--5">Coming soon!</span>
                                                </div>
                                            </div>
                                        </ListItem>
        
                                        <ListItem flex pointer @click="duplicateProject">
                                            <IconSvg name="duplicate" />
                                            <span class="ml--10 text--sm text-gray-800">Duplicate</span>
                                        </ListItem>

                                        <ListItem flex pointer @click="showArchiveModal = true">
                                            <IconSvg name="archive" />
                                            <span class="ml--10 text--sm text-gray-800">Archive</span>
                                        </ListItem>

                                        <ListItem flex pointer @click="toggleFavourite">
                                            <IconSvg
                                                :name="`${currentProject.isFavourite ? 'star-solid' : 'star'}`" 
                                                :fill=" `${currentProject.isFavourite ? '#FFBA0A' : '#667085'}`"
                                                :disabled="appState === 'update_favourite_loading'"
                                            />
                                            <span class="ml--10 text--sm text-gray-800">{{ currentProject.isFavourite ? 'Remove from' : 'Add to ' }} favorites</span>
                                        </ListItem>  
        
                                        <li><hr class="dropdown-divider dropdown--divider" style="color: #bcc1c6;"></li>
        
                                        <FormLabel title="Reminder actions" label-type="sm_mono" class="pl--10 dropdown-item-label text--medium" />
        
                                        <ListItem flex pointer>
                                            <div>
                                                <div class="flex align-items-center">
                                                    <FormSwitch 
                                                        v-model="currentProject.enableReminder" 
                                                        :true-value="true"
                                                        :false-value="false"
                                                        label-class="ml--10 text--sm text-gray-800 mb--0 pb--0"
                                                        @input="toggleEnableReminders" 
                                                    >
                                                        {{ `${currentProject.enableReminder ? 'Disable' : 'Enable'}` }} reminder
                                                    </FormSwitch>
                                                </div>
                                            </div>
                                        </ListItem>
        
                                        <li><hr class="dropdown-divider dropdown--divider" style="color: #bcc1c6;"></li>
        
                                        <FormLabel title="Tracker actions" label-type="sm_mono" class="pl--10 dropdown-item-label text--medium" />
                                        
                                        <ListItem
                                            v-if="!['completed', 'to do'].includes(currentProject.status)" 
                                            flex 
                                            pointer 
                                            :class="{ 'disabled': appState === 'track_project_loading' }"
                                            @click="toggleTracking"
                                        >
                                            <IconSvg :name="isTracking ? 'stop-circle' : 'play-circle'" />
                                            <span class="ml--10 text--sm text-gray-800">{{ isTracking ? 'Stop' : 'Start' }} tracker</span>
                                        </ListItem>
        
                                        <ListItem
                                            v-if="showScheduledTrackerOpts" 
                                            flex 
                                            pointer 
                                            :class="{ 'disabled': appState === 'track_project_loading' }"
                                            @click="modalActionType = 'showTimeTrackingModal'"
                                        >
                                            <IconSvg name="bell" />
                                            <span class="ml--10 text--sm text-gray-800">Manage trackers</span>
                                        </ListItem>
        
                                        <li><hr class="dropdown-divider dropdown--divider" style="color: #bcc1c6;"></li>
        
                                        <ListItem flex pointer @click="modalActionType = 'showDeleteModal'">
                                            <IconSvg name="delete" fill="#e04060" />
                                            <span class="ml--10 text--sm text-error-400">Delete project</span>
                                        </ListItem>
                                    </List>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="project__details__stats">
                    <div class="project__details__stats__item">
                        <div class="flex align-items-center justify-content-between">
                            <p class="title">
                                Current time:
                            </p>
                        </div>
                        <h5>{{ formattedTime }} {{ !formattedTime ? 'hrs' : '' }}</h5>
                    </div>

                    <div class="project__details__stats__item">
                        <p class="title">
                            Total overtime hours:
                        </p>
                        <h5>00:00</h5>
                    </div>
                    <div class="project__details__stats__item">
                        <p class="title">
                            Total overtime cost:
                        </p>
                        <h5>{{ $country.getSymbol(currentProject.currency, 'currency') || "" }}0.00</h5>
                    </div>

                    <div class="project__details__stats__item">
                        <p class="title">
                            Total hours:
                        </p>
                        <h5>{{ setTime(totalTimeTracked) }}</h5>
                    </div>

                    <div class="project__details__stats__item">
                        <div class="flex align-items-center justify-content-between">
                            <p class="title">
                                Estimated revenue:
                            </p>
                            <BaseButton 
                                variant="ghost" 
                                label="Edit" 
                                medium
                                class="p--0 min-h-auto" 
                                @submit="toggleRevenue"            
                            />
                        </div>
                        <h5>{{ $country.getSymbol(currentProject.currency, 'currency') }}{{ $format_amount(currentProject.estimatedRevenue, 2) }}</h5>
                    </div>
                </div>

                <div>
                    <div class="invoice__details__row">
                        <div class="invoice__details__row__left project__details__row__left">
                            <div class="invoice__details__item pb--10 pt--0">
                                <div class="w-100 mb--10">
                                    <div class="col-12 mb--10 flex align-items-center mt--5 justify-content-between">
                                        <FormLabel title="Website links" html-for="websiteLinks" label-type="title" />
                                    </div>

                                    <div class="col-12 flex align-items-end justify-content-between">
                                        <div class="form__row">
                                            <FormInput 
                                                id="linkName" 
                                                v-model="linkForm.name"
                                                type="text" 
                                                size="sm" 
                                                wrapper-class="flex-1"
                                                info-type="required"
                                                no-margin-bottom
                                                html-for="linkName" 
                                                label="Link name"
                                            />
                                            <FormInput 
                                                id="linkUrl" 
                                                v-model="linkForm.url"
                                                type="text" 
                                                size="sm" 
                                                wrapper-class="flex-1"
                                                info-type="required"
                                                no-margin-bottom
                                                html-for="linkUrl" 
                                                label="Link URL"
                                            />
                                        </div>
                                        <BaseButton 
                                            variant="ghost" 
                                            label="+ Add link"
                                            :disabled="!linkForm.url || !linkForm.name"
                                            :loading="linkState === 'add_link_loading'"
                                            class="p--0 ml--10 max-h-fit"
                                            @submit="addProjectLink"
                                        />
                                    </div>

                                    <div>
                                        <template v-if="linkState === 'fetch_link_loading'">
                                            <PageSpinner size="sm" />
                                        </template>
    
                                        <template v-else-if="linkState === 'fetch_link_error'">
                                            <EmptyPage state="error" @action="handleFetchProjectLinks" />
                                        </template>
    
                                        <template v-else>
                                            <div v-if="projectLinks.length" class="mt--30">
                                                <div 
                                                    v-for="link in projectLinks" 
                                                    :key="link._id" 
                                                    class="mb--10 flex align-items-center justify-content-between w-100 flex-wrap"
                                                    :class="{ 'disabled': editLinkForm?._id === link._id }"
                                                >
                                                    <div class="flex align-items-center">
                                                        <a class="project__item__title text--normal mt--0 text--sm text--underline" :href="link.url">
                                                            {{ link.name }}
                                                        </a>
                                                    </div>
                                                    
                                                    <div class="flex align-items-center">
                                                        <BaseButton 
                                                            variant="ghost" 
                                                            variant-style="secondary"
                                                            class="p--0 mr--10"
                                                            @submit="openEditLinkModal(link)"
                                                        />
                                                        <BaseButton 
                                                            variant="ghost" 
                                                            variant-style="secondary"
                                                            class="p--0"
                                                            :loading="linkState === 'delete_link_loading' && linkIdToDelete === link._id"
                                                            @submit="deleteLink(link._id)"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
        
                                            <div v-else />
                                        </template>    
                                    </div>
                                </div>
                            </div>

                            <div class="invoice__details__item pb--10 pt--10">
                                <div class="col-12 mb--10 flex align-items-center justify-content-between">
                                    <FormLabel title="Estimate & billing details" html-for="meta" label-type="title" />
                                    <BaseButton 
                                        variant="ghost" 
                                        label="Edit" 
                                        class="p--0 mr--10 text--semi--bold"
                                        @submit="openEditModal('billingDetails')"            
                                    />
                                </div>

                                <div class="invoice__details__group mb--10">
                                    <div>
                                        <FormLabel title="Deadline" html-for="meta" label-type="menu" />
                                        <p class="invoice__details--text text--sm">
                                            {{ $format_date(currentProject.deadline) || 'N/A' }}
                                        </p>
                                    </div>                                    
                                </div>

                                <div class="invoice__details__group mb--10">
                                    <div>
                                        <FormLabel title="Currency" html-for="meta" label-type="menu" />
                                        <p class="text--upper invoice__detail text--sm">
                                            {{ currentProject.currency || '-' }}
                                        </p>
                                    </div>
                                </div>

                                <div class="invoice__details__group mb--10">
                                    <div>
                                        <FormLabel title="Payment status" html-for="meta" label-type="menu" />
                                        <p class="text--upper invoice__detail text--sm">
                                            {{ currentProject.projectType }}
                                        </p>
                                    </div>
                                </div>

                                <div class="invoice__details__group mb--10">
                                    <div>
                                        <FormLabel title="Billable hours" html-for="meta" label-type="menu" />
                                        <p class="text--capitalize invoice__detail text--sm">
                                            {{ currentProject.billableHours || 0 }} hours
                                        </p>
                                    </div>
                                </div>

                                <div class="invoice__details__group">
                                    <div>
                                        <FormLabel title=" Non-billable hours" html-for="meta" label-type="menu" />
                                        <p class="text--capitalize invoice__detail text--sm">
                                            {{ currentProject.nonBillableHours || 0 }} hours
                                        </p>
                                    </div>
                                </div>

                                <div class="invoice__details__group">
                                    <div>
                                        <FormLabel title="Billable Rate" html-for="meta" label-type="menu" />
                                        <p class="text--upper invoice__detail text--sm">
                                            {{ currentProject.billingRate || 0 }} %
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div class="invoice__details__item pb--10 pt--10">
                                <div class="col-12 mb--10 flex align-items-center justify-content-between">
                                    <FormLabel title="Tracking details" html-for="meta" label-type="title" />
                                    <BaseButton 
                                        variant="ghost" 
                                        label="Edit" 
                                        class="p--0 mr--10 text--semi--bold"
                                        @submit="openEditModal('trackingDetails')"            
                                    />
                                </div>

                                <div class="invoice__details__group">
                                    <div>
                                        <FormLabel title="Tracking Type" html-for="meta" label-type="menu" />
                                        <p class="text--capitalize invoice__detail text--sm">
                                            {{ currentProject.trackingType && currentProject.trackingType === "scheduled_tracker" ? 'scheduled' : currentProject.trackingType || "-" }}
                                        </p>
                                    </div>
                                </div>

                                <div class="invoice__details__group">
                                    <div>
                                        <FormLabel title="Track project" html-for="meta" label-type="menu" />
                                        <p class="text--capitalize invoice__detail text--sm">
                                            {{ currentProject.trackProject ? "Yes" : "No" }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div class="col-12">
                                <FormLabel title="Invoices" html-for="meta" label-type="title" class="mt--20 mb--15" />
                                <ProjectInvoices :current-project="currentProject" />
                            </div>
                        </div>

                        <div class="invoice__details__row__right">
                            <div class="invoice__details__divider pb--15">
                                <div class="flex align-items-center justify-content-between mt--5 mb--10">
                                    <div class="flex align-items-center">
                                        <FormLabel title="Linked Clients" html-for="clients" label-type="menu" class="mr--5 mb--0" />
                                        <span class="badge rounded-pill tag--grey text-dark">
                                            {{ currentProject.clients && currentProject.clients.length || 0 }}
                                            <span class="visually-hidden">clients count</span>
                                        </span>
                                    </div>

                                    <BaseButton 
                                        variant="ghost" 
                                        label="Manage" 
                                        medium
                                        class="p--0"
                                        @submit="showClientsModal = true"
                                    />
                                </div>

                                <template v-if="currentProject.clients && currentProject.clients.length">
                                    <div class="">
                                        <router-link 
                                            v-for="client in currentProject.clients" 
                                            :key="client._id" 
                                            class="block project__item__desc text--underline mr--10"
                                            :to="`/dashboard/clients/${client._id}`"
                                        >
                                            {{ client.name }}
                                        </router-link>
                                    </div>
                                </template>

                                <template v-else>
                                    <p class="project__item__desc">
                                        No clients added yet.
                                    </p>
                                </template>
                            </div>

                            <div class="invoice__details__divider pb--15">
                                <div class="flex align-items-center justify-content-between mt--10 mb--10">
                                    <div class="flex align-items-center">
                                        <FormLabel title="Reminders" html-for="reminders" label-type="menu" class="mr--5 mb--0" />
                                        <span class="badge rounded-pill tag--grey text-dark">
                                            {{ reminders && reminders.length || 0 }}
                                            <span class="visually-hidden">reminders count</span>
                                        </span>
                                    </div>

                                    <BaseButton 
                                        variant="ghost" 
                                        label="Manage" 
                                        medium
                                        class="p--0"
                                        @submit="openRemindersModal"
                                    />
                                </div>

                                <template v-if="reminders && reminders.length">
                                    <div class="grid gap-1">
                                        <div v-for="reminder in reminders" :key="reminder" class="flex align-items-center">
                                            <span class="mr--5 rounded-circle" style="width: 10px; height: 10px; border: 1px dotted grey;" />
                                            <span class="text--sm text-gray-600">
                                                {{ mapReminders[reminder] }}
                                            </span>
                                        </div>
                                    </div>
                                </template>

                                <template v-else>
                                    <p class="project__item__desc">
                                        No reminders added
                                    </p>
                                </template>
                            </div>

                            <div class="invoice__details__divider pb--15">
                                <div class="flex align-items-center justify-content-between mt--10 mb--10">
                                    <FormLabel title="Rate card" html-for="meta" label-type="menu" class="mb--0" />
                                    <BaseButton 
                                        variant="ghost" 
                                        label="Manage" 
                                        medium
                                        class="p--0"
                                        @submit="modalActionType = 'showRateCardsModal'"
                                    />
                                </div>

                                <template v-if="currentProject.ratecard">
                                    <router-link :to="`/dashboard/rate-cards/${currentProject.ratecard._id}`" target="_blank" class="project__item__desc mb--5 text--underline">
                                        {{ currentProject.ratecard.title }}
                                    </router-link>
                                </template>

                                <template v-else>
                                    <p class="project__item__desc">
                                        No rate card added
                                    </p>
                                </template>
                            </div>

                            <ProjectNotes />
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <ConfirmDeletion
            type="project" 
            custom-msg 
            :loading="deleteState.loading" 
            :show-modal="modalActionType === 'showDeleteModal'" 
            @cancel="toggleDelete"
            @delete="handleDeleteProject"
        >
            <template #custom-msg>
                <p class="text--sm text-gray-800">
                    All data for this project will be permamently removed but its associated clients and invoices will remain. <strong>This action cannot be undone.</strong>
                </p>
            </template>
        </ConfirmDeletion>

        <CreateProjectModal
            :show-modal="showEditModal" 
            action-type="edit" 
            :current-selection="editModalType" 
            :loading="modalSubmitBtn === 'projectUpdate'"
            :current-project="currentProject" 
            @close="showEditModal = false" 
            @complete="handleUpdateProject"
        />

        <ArchiveRecord 
            type="project" 
            :loading="pageLoading" 
            :show-modal="showArchiveModal" 
            @cancel="showArchiveModal = false" 
            @archive="handleArchiveProject"
        />

        <ManageRemindersModal 
            :initial-reminders="reminders" 
            :loading="modalSubmitBtn === 'manageReminders'" 
            :show-modal="showRemindersModal"
            :reload="currentProject.trackProject"
            @cancel="showRemindersModal = false" 
            @complete="manageReminders"
        />

        <MainModal 
            :show-modal="showClientsModal"
            size="sm" 
            title="Manage clients" 
            @close="resetClientSelection"
        >
            <template #body>
                <form class="pb--5" @submit.prevent="">
                    <CustomDropdown 
                        id="clientDropdown" 
                        v-model="form.clients" 
                        :options="clients"
                        multiple
                        list-type="clients" 
                        index-label="name" 
                        :loading="searchItemsLoading"
                        @search="searchClients"
                        @select="selectClient" 
                        @remove="removeClientSelection"
                    />
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" 
                        label="Cancel" 
                        variant="outline" 
                        @submit="resetClientSelection"
                    />
                    <BaseButton 
                        type="submit" 
                        :loading="modalSubmitBtn === 'manageClients'" 
                        label="Update" 
                        @submit="manageClients"
                    />
                </div>
            </template>
        </MainModal>

        <MainModal 
            :show-modal="modalActionType === 'showRateCardsModal'"
            size="sm" 
            title="Convert rate card to invoice" 
            @close="resetRateCardSelection"
        >
            <template #body>
                <Alert type="secondary" classes="text--sm mb--20">
                    <template #text>
                        <p class="text--sm">
                            Adding a rate card to a project converts it to an invoice. Use this option to create new invoice items from the selected rate card data.
                        </p>
                    </template>
                </Alert>

                <form @submit.prevent="">
                    <CustomDropdown 
                        id="rateCardDropdown" 
                        v-model="form.rateCard" 
                        :options="rateCards" 
                        list-type="rate cards" 
                        index-label="title" 
                        :loading="searchItemsLoading"
                        @search="searchRateCards" 
                        @select="selectRateCard" 
                    />
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center gap-1">
                    <BaseButton 
                        label="Cancel" 
                        variant="outline" 
                        @submit="resetRateCardSelection"
                    />
                    <BaseButton 
                        type="submit" 
                        label="Convert" 
                        @submit="convertRateCard"
                    />
                </div>
            </template>
        </MainModal>

        <ManageTimeTrackingModal 
            :show-modal="modalActionType === 'showTimeTrackingModal'" 
            :current-project="currentProject" 
            @cancel="modalActionType = ''"
        />

        <UpgradeAccountModal 
            :show-modal="showUpgradeModal || modalActionType === 'showUpgradeModalAlt'" 
            type="projects"
            @cancel="modalActionType = ''"
        >
            <template #text>
                <div>
                    <template v-if="accountPlan === 'free'">
                        <p class="text--sm text-gray-600">
                            Project {{ upgradeActionType }} is a paid feature available on the <strong>Standard</strong> and <strong>Plus</strong> plans.
                        </p>
                        <p class="text--sm text-gray-600 mt--15">
                            To unlock this feature, upgrade your account in your account settings.
                        </p>
                    </template>
                    <template v-if="accountPlan === 'standard'">
                        <p class="text--sm text-gray-600">
                            You have reached the {{ upgradeActionType }} limit allowed on your account plan. Upgrade to our <strong>Plus</strong> plan to access unlimited {{ upgradeActionType }}.
                        </p>
                        <p class="text--sm text-gray-600 mt--15">
                            To unlock this feature, upgrade your account in your account settings.
                        </p>
                    </template>
                </div>
            </template>
        </UpgradeAccountModal>

        <MainModal 
            :show-modal="modalActionType === 'showEditLinkModal'"
            size="md" 
            title="Manage link" 
            @close="closeEditLinkModal"
        >
            <template #body>
                <form @submit.prevent="">
                    <div class="form__row">
                        <FormInput 
                            id="linkName" 
                            v-model="editLinkForm.name"
                            type="text" 
                            size="sm" 
                            wrapper-class="flex-1"
                            info-type="required"
                            no-margin-bottom
                            html-for="linkName" 
                            label="Link name"
                        />
                        <FormInput 
                            id="linkUrl" 
                            v-model="editLinkForm.url"
                            type="text" 
                            size="sm" 
                            wrapper-class="flex-1"
                            info-type="required"
                            no-margin-bottom
                            html-for="linkUrl" 
                            label="Link URL"
                        />
                    </div>
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" 
                        label="Cancel" 
                        variant="outline" 
                        :disabled="linkState === 'edit_link_loading'" 
                        @submit="closeEditLinkModal"
                    />
                    <BaseButton 
                        type="submit" 
                        :loading="linkState === 'edit_link_loading'" 
                        :disabled="!editLinkForm.name || !editLinkForm.url" 
                        label="Update" 
                        @submit="editLink"
                    />
                </div>
            </template>
        </MainModal>

        <MainModal 
            :show-modal="modalActionType === 'showStartWarningModal'"
            size="sm" 
            :footer="false"
            @close="modalActionType = ''"
        >
            <template #body>
                <div class="flex align-items-center justify-content-center success__img__wrap">  
                    <div class="rounded-image-wrap">
                        <IconSvg name="lock" width="32px" height="32px" fill="#560a43" />
                    </div>                      
                </div>

                <div class="mx-auto mt--30 mb--25">
                    <div>
                        <p class="text-center text--md text--semi--bold text-gray-800">
                            Discard existing schedule?
                        </p>

                        <p class="text-center text-gray-600 mt--10 text--sm">
                            This project has been scheduled to start on <strong>{{ $format_date(currentProject.startDate) }}</strong>.
                        </p>

                        <p class="text-center text-gray-600 mt--15 text--sm">
                            Clicking on <strong>"Continue"</strong> automatically starts this project and discards the already scheduled time.
                        </p>
                    </div>
                </div>
                
                <div class="w-100 flex justify-content-end align-items-center mb--10">
                    <BaseButton 
                        class="mr--10" 
                        label="Cancel" 
                        variant="outline" 
                        @submit="modalActionType = ''"
                    />
                    <BaseButton 
                        type="submit" 
                        :loading="modalSubmitBtn === 'replacePreviousTracking'" 
                        label="Continue" 
                        @submit="replacePreviousTracking"
                    />
                </div>
            </template>
        </MainModal>

        <MainModal 
            :show-modal="modalActionType === 'shareProjectModal'"
            size="sm" 
            title="Share project"
            @close="modalActionType = ''"
        >
            <template #body>
                <div>
                    <div 
                        v-for="item in sharePlatform" 
                        :key="item.id" 
                        class="w-100 project__share__item pointer"
                        :class="{ 'selected': shareProjectSelection === item.id }"
                    >
                        <div class="form-check form-check-inline mr--0 flex justify-content-between align-items-center pl--0">
                            <label class="form-check-label text--sm" :for="`inlineRadio${item.name}`">
                                <div class="flex align-items-center">
                                    <div class="project__share__icon">
                                        <IconSvg :name="item.iconName" />
                                    </div>
                                    <div class="ml--15 project__share__text">
                                        <p class="text--sm text--semi--bold text-gray-600">
                                            Share on {{ item.name }}
                                        </p>
                                        <p class="mt--3 text--xs text-gray-600">
                                            This project will be shared publicly
                                        </p>
                                    </div>
                                </div>
                            </label>
                            <input
                                :id="`inlineRadio${item.id}`" 
                                v-model="shareProjectSelection"
                                class="form-check-input mr--0" 
                                type="radio"
                                :name="`inlineRadio${item.id}`" 
                                :value="item.id"
                            >
                        </div>
                    </div>
                </div>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--10" 
                        label="Cancel" 
                        variant="outline" 
                        @submit="modalActionType = ''"
                    />
                    <BaseButton 
                        type="submit" 
                        label="Continue" 
                        :disabled="!shareProjectSelection"
                        :loading="modalSubmitBtn === 'shareProject'"
                        @submit="shareProject"
                    />
                </div>
            </template>
        </MainModal>

        <UpgradeAccountAlt 
            :show-modal="modalActionType === 'showUpgradeModalAlttt'"    
            :title="upgradeCopy.title" 
            :subtitle="upgradeCopy.subtitle" 
            show-paid-plan-badge
            @cancel="closeUpgradeModalAlt"
        />

        <ScheduleProjectModal :show-modal="modalActionType === 'showStartProjectModal'" :project-id="currentProject?._id" @close="modalActionType = ''" />
    </div>
</template>

<script>
import {
    BaseButton,
    Status,
    EmptyPage,
    PageSpinner,
    CreateProjectModal,
    UpgradeAccountModal,
    ArchiveRecord, 
    ManageTimeTrackingModal,
    ConfirmDeletion,
    IconSvg, 
    CustomDropdown,
    ManageRemindersModal,
    MainModal, 
    FormLabel,
    FormInput,
    FormSwitch,
    List,
    ListItem,
    Alert,
} from "@/components/ui/index";
import { projectFilterStatuses, priorities, MAX_COUNT_BY_COLLECTIONS, MAX_COUNT_BY_FEATURES, projectStatusMap } from "@/utils/enums";
import ProjectInvoices from "@/components/projects/ProjectInvoices.vue";
import ProjectNotes from "@/components/projects/ProjectNotes.vue";
import { completedProjectMarkers, mapReminders, sharePlatform } from "@/utils/dummy";
import { assembleQueryList, debounce } from "@/utils/helpers";
import UpgradeAccountAlt from "@/components/ui/modals/UpgradeAccountAlt.vue";
import ScheduleProjectModal from "@/components/ui/modals/ScheduleProjectModal.vue";
import TipTap from "@/components/ui/editor/TipTap.vue";

export default {
    name: "ProjectDetailsView",

    components: {
        IconSvg,
        BaseButton,
        Status, 
        MainModal,
        EmptyPage,
        PageSpinner,
        ProjectInvoices,
        UpgradeAccountModal,
        CreateProjectModal,
        CustomDropdown,
        ArchiveRecord,
        ConfirmDeletion, 
        FormLabel,
        ManageTimeTrackingModal,
        ProjectNotes, 
        ManageRemindersModal,
        FormInput,
        FormSwitch,
        List,
        ListItem,
        Alert,
        UpgradeAccountAlt,
        ScheduleProjectModal,
    },

    data() {
        return {
            pageLoading: false,
            pageLoadingErr: false,
            pageLoadingErrMsg: "",
            form: {
                clients: [],
            },
            deleteState: {
                loading: false,
                errMsg: "",
                err: false,
            },
            showEditModal: false,
            projectFilterStatuses,
            projectStatusMap,
            mapReminders,
            priorities,
            sharePlatform,
            projectSubType: "",
            showArchiveModal: false,
            showRemindersModal: false,
            reminders: [],
            editModalType: "",
            currentProject: {},
            searchItemsLoading: false,
            searchItemsErr: false,
            showClientsModal: false,
            clients: [],
            rateCards: [],
            logs: [],
            showProjectSettingsModal: false,
            events: ["mousemove", "keydown", "click", "scroll", "visibilitychange"],
            isTracking: false,
            elapsedTime: 0,
            activityLog: [],
            timer: null,
            lastActive: Date.now(),
            idleCheckTimer: null,
            upgradeActionType: "",
            logItem: {
                _id: "",
                startTime: 0,
                endTime: 0,
            },
            linkForm: {
                url: "",
                name: "",
            },
            editLinkForm: {
                url: "",
                name: "",
            },
            fetchLinksLoading: false,
            fetchLinksLoadingErr: false,
            linkState: "link_default_state",
            projectLinks: [],
            linkIdToDelete: "",
            modalActionType: "",
            shareProjectSelection: "",
            modalSubmitBtn: "",
            appState: "",
            deadlineState: {
                copy: "",
                color: "",
                isDue: false,
            },
            projectTrackerHandler: null,
            rateCard: undefined,
        };
    },

    computed: {
        formattedTime() {
            const seconds = Math.floor((this.elapsedTime / 1000) % 60);
            const minutes = Math.floor((this.elapsedTime / (1000 * 60)) % 60);
            const hours = Math.floor(this.elapsedTime / (1000 * 60 * 60));
            
            return [hours, minutes, seconds]
                .map(n => n.toString().padStart(2, "0"))
                .join(":");
        },

        timeTrackingCopy() {
            if (this.accountPlan === "free") {
                return "Upgrade to add time tracking";
            } 
            
            if (this.currentProject.trackProject) {
                return "Disable time tracking";
            } else {
                return "Enable time tracking";
            }
        },

        storedClients() {
            return this.$store.getters["clients/clients"];
        },

        storedRateCards() {
            return this.$store.getters["ratecards/rateCards"];
        },

        showUpgradeModal() {
            return this.$store.getters["app/showUpgradeModal"];
        },

        checkUpgradeLoading() {
            return this.$store.getters["app/checkUpgradeLoading"];
        },

        checkUpgradeFields() {
            return this.$store.getters["app/checkUpgradeFields"];
        },
        
        completedProjectCount() {
            return this.$store.getters["app/completedProjectCount"];
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        projects() {
            return this.$store.getters["project/projects"];
        },

        projectTotal() {
            return this.$store.getters["project/pageData"]?.total;
        },

        projectRemindersCount() {
            return this.$store.getters["auth/profile"]?.reminders.find(x => x.id === "projects")?.count;
        },

        activityLogs() {
            return this.$store.getters["project/timeLogs"].list;
        },

        totalTimeTracked() {            
            return this.activityLogs?.reduce((previous, current) => {
                const duration = (current.endTime || Date.now()) - current.startTime;                
                return previous + duration;
            }, 0);
        },

        upgradeCopy() {
            switch (this.upgradeActionType) {
            case "time_tracking":
                return {
                    title: "Start time tracking",
                    subtitle: "Time tracking for projects is a great way to record time taken to execute a project, whether for record keeping or growth setting.",
                };
            case "project_reminders_free":
                return {
                    title: "Automate project deadline reminders",
                    subtitle: "",
                };
            case "project_reminders_exhausted":
                return {
                    title: "You have exhausted your reminders count",
                    subtitle: "The number of reminders allowed on your account plan has been exhausted",
                };
            
            default:
                return {
                    title: "",
                    subtitle: "",
                };
            }
        },

        showScheduledTrackerOpts() {
            if (this.currentProject.status === "completed" || this.currentProject.status === "archived") return false;

            if (this.isTracking) return false;
            
            return true;
        },
    },

    watch: {
        "showClientsModal": {
            async handler(val) {
                if (val) {
                    if (this.storedClients?.length) {
                        this.clients = this.storedClients;
                        return;
                    }

                    if (!this.storedClients?.length && !this.clients?.length) {
                        return await this.searchClients();
                    }
                }
            },
        },

        "modalActionType": {
            async handler(val) {
                if (val === "showRateCardsModal") {                    
                    if (this.storedRateCards?.length) {
                        this.rateCards = this.storedRateCards;
                        return;
                    }

                    if (!this.storedRateCards?.length && !this.rateCards?.length) {
                        return await this.searchRateCards();
                    }
                }
            },
        },

        "currentProject.trackProject": {
            async handler(newVal) {                
                console.log({ "currentProject.trackProject": newVal });
                await this.watchTracker(newVal);
            },
            immediate: true,
            deep: true,
        },

        "completedProjectCount": {
            async handler(newVal) {
                if (completedProjectMarkers.includes(newVal)) {                    
                    this.$store.dispatch("app/toggleConfetti", {
                        show: true,
                        type: "rank",
                    });
                }
            },
        },

        "currentProject.deadline": {
            handler(newVal) {                
                this.computeDeadlineStatus(newVal);
            },
            immediate: true,
            deep: true,
        },
    },

    created() {
        this.setupEventListeners();
    },

    beforeDestroy() {
        this.stopTimer();
        this.removeEventListeners();

        if (this.projectTrackerHandler) {
            this.$socketOff("projectTracker", this.projectTrackerHandler);
        }
    },

    async mounted() {
        this.$socketOn("projectTracker", (data) => {
            console.log("Updated subscription:", data);
            this.$store.dispatch("project/setNewTracker", data);
        });

        await this.handleFetchProject();

        const projectId = this.$route.params.id;   
        // await Promise.all([
        //     this.$store.dispatch("project/fetchLogs", { projectId }),
        //     this.watchTracker(true),
        //     this.handleFetchProjectLinks()
        // ])             
        await this.$store.dispatch("project/fetchLogs", { projectId });
        await this.watchTracker(true);

        await this.handleFetchProjectLinks();

        if (this.currentProject.deadline) {
            this.computeDeadlineStatus(this.currentProject.deadline);
        }
    },

    methods: {
        computeDeadlineStatus(newVal) {            
            if (!newVal) {
                this.deadlineState = {
                    copy: "",
                    color: "",
                    isDue: false,
                };
                return;
            };

            const dueDate = new Date(newVal).getTime();
            const currentDate = Date.now();

            const isDue = dueDate < currentDate;
            const isDueSoon = dueDate < (currentDate + (7 * 24 * 60 * 60 * 1000)); // 7 days
            const isDueToday = new Date(newVal).toDateString() === new Date().toDateString();

            const daysOverdue = Math.ceil((currentDate - dueDate) / (1000 * 60 * 60 * 24));

            console.log({ isDue, isDueSoon, isDueToday, daysOverdue });

            if (isDue || isDueSoon) {
                this.deadlineState.isDue = true;
            }

            if (isDue) {
                if (!isDueToday) {
                    this.deadlineState.copy = `overdue by ${daysOverdue} days`;
                    this.deadlineState.color = "danger";
                } else {
                    this.deadlineState.copy = "due today";
                    this.deadlineState.color = "warning";
                }
                return;
            } 

            if (isDueSoon) {                
                if (daysOverdue === 0 || daysOverdue < 1) {
                    this.deadlineState.color = "warning";
                    this.deadlineState.copy = "due today";
                    
                } else {
                    this.deadlineState.color = "light";
                }
                return `due in ${daysOverdue} days`;
            }
        },

        closeUpgradeModalAlt() {
            this.modalActionType = "";
            this.upgradeActionType = "";
        },

        watchTracker(val) {            
            if (val) {
                const currentTrackingLog = this.activityLogs[0];

                if (currentTrackingLog?.startTime && !currentTrackingLog?.endTime) {
                    // this.elapsedTime = Date.now() - new Date(currentTrackingLog.startTime).getTime();
                    this.elapsedTime = Date.now() - currentTrackingLog.startTime;
                    this.isTracking = true;
                    this.logItem = currentTrackingLog;
                    
                    // this.timer = setInterval(this.elapsedTime, 1000);
                    this.timer = setInterval(this.updateTimer, 1000);
                }
            } else {                
                clearInterval(this.timer);
                clearInterval(this.idleCheckTimer);

                this.elapsedTime = 0; 
                this.isTracking = false;
                this.logItem = {
                    _id: "",
                    startTime: 0,
                    endTime: 0,
                };
            }
        },

        setTime(val) {
            const seconds = Math.floor((val / 1000) % 60);
            const minutes = Math.floor((val / (1000 * 60)) % 60);
            const hours = Math.floor(val / (1000 * 60 * 60));
        
            const duration = [hours, minutes, seconds]
                .map(n => n.toString().padStart(2, "0"))
                .join(":");

            return duration;
        },

        async handleFetchProjectLinks() {            
            try {                
                if (!this.$route.params.id) {
                    this.$toast.error("Unable to fetch project links");
                    return;
                }

                this.linkState = "fetch_link_loading";
                this.projectLinks = await this.$store.dispatch("project/fetchLinks", this.$route.params.id);
            } catch (error) {
                this.linkState = "fetch_link_error";
            } finally {
                this.linkState = "link_default_state";
            }
        },

        openEditLinkModal(item) {
            this.editLinkForm = item;
            this.modalActionType = "showEditLinkModal";
        },

        closeEditLinkModal() {
            this.editLinkForm = {
                name: "",
                url: "",
            };
            this.modalActionType = "";
        },

        async editLink() {            
            try {                
                if (!this.$route.params.id) {
                    this.$toast.error("Unable to update link");
                    return;
                }

                this.linkState = "edit_link_loading";

                const res = await this.$store.dispatch("project/manageLinks", {
                    projectId: this.$route.params.id,
                    payload: {
                        name: this.editLinkForm.name,
                        url: this.editLinkForm.url,
                        _id: this.editLinkForm?._id,
                    },
                    params: { type: "update" },
                });
                
                this.projectLinks = res;
                this.closeEditLinkModal();
            } catch (error) {
                this.linkState = "edit_link_error";
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.linkState = "link_default_state";
            }
        },

        async deleteLink(id) {            
            try {                                
                if (!id || !this.$route.params.id) {
                    this.$toast.error("Unable to delete link");
                    return;
                }

                this.linkIdToDelete = id;
                this.linkState = "delete_link_loading";
                this.projectLinks = await this.$store.dispatch("project/manageLinks", {
                    projectId: this.$route.params.id,
                    payload: { linkId: id },
                    params: { type: "delete" },
                });

            } catch (error) {
                this.linkState = "delete_link_error";
            } finally {
                this.linkState = "link_default_state";
            }
        },

        async addProjectLink() {            
            try {                
                if (!this.$route.params.id) {
                    this.$toast.error("Unable to add link");
                    return;
                }

                this.linkState = "add_link_loading";
                
                const res = await this.$store.dispatch("project/manageLinks", {
                    projectId: this.$route.params.id,
                    payload: {
                        name: this.linkForm.name,
                        url: this.linkForm.url,
                    },
                    params: { type: "add" },
                });

                this.linkForm = {
                    name: "",
                    link: "",
                };
                
                this.projectLinks = res;
            } catch (error) {
                this.linkState = "add_link_error";
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            } finally {
                this.linkState = "link_default_state";
            }
        },

        closeUpgradeModal() {
            if (this.modalActionType === "showUpgradeModalAlt") {
                this.modalActionType = "";
                return;
            }
            this.$store.dispatch("app/closeUpgradeModal", "projects");
        },

        goToTasks() {
            this.$router.push({ path: `/dashboard/projects/${this.$route.params.id}/tasks`});
        },

        selectClient(val) {
            const item = this.form.clients.find(x => x?._id === val?._id);

            if (!item) {
                this.form.clients = [ ...this.form.clients, val];
            };
        },

        removeClientSelection(item) {
            this.form.clients = this.form.clients.filter(x => x?._id !== item?._id);
        },

        selectRateCard(val) {
            this.rateCard = val;
        },

        convertRateCard(item) {
            this.$store.dispatch("project/setSelectedProject", this.currentProject);
            this.$store.dispatch("project/setInvoice", {
                currency: item.currency || "",
                meta: {
                    // tax: [{ taxName: "vat", taxType: "flat", taxValue: 0 }],
                    tax: [],
                    items: [{ itemName: "", itemPrice: 0.00, itemQuantity: 0, itemUnit: 0.00 }],
                },
                status: "draft",
            });
            this.modalActionType = "";
        },
        
        async handleFetchProject() {
            try {
                const id = this.$route.params.id;

                if (id === "undefined" || !id) {
                    return this.$route.push("/dashboard/projects");
                }

                this.pageLoadingErr = false;
                this.pageLoadingErrMsg = "";
                this.pageLoading = true;

                const res = await this.$store.dispatch("project/getProject", id);
                this.currentProject = res;
                this.reminders = res.reminders;
                this.form.clients = res.clients;
                this.form.rateCard = res.ratecard;
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;

                this.pageLoadingErr = true;
                this.$toast.error(errMsg);
            } finally {
                this.pageLoading = false;
            }
        },

        async onCompleteSubUpdate(resp) {
            if (!resp.key) return;

            this.currentProject = { ...this.currentProject, [`${resp.key}`]: resp.list };
            this.closeSubModal();
        },

        async handleStatusUpdate(val) {
            try {
                this.appState = "update_status_loading";

                if (val === this.currentProject.status) return;

                const tempStatus = this.currentProject.status;

                await this.singularUpdateProject({ payload: { status: val } });

                // await this.$store.dispatch("project/updateProject", { 
                //     id: this.currentProject._id, 
                //     payload: { 
                //         status: val,
                //     }, 
                // });
                
                if (val === "completed" || tempStatus === "completed") {
                    this.$store.dispatch("app/fetchCompletedProjectCount", { refresh: true });
                }     
            } catch (error) {
                const errMsg =error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.appState = "";
            }
        },

        // TO DO: fix slight delay when toggling the favorite feature
        async toggleFavourite() {
            try {
                this.appState = "update_favourite_loading";
                const val = !this.currentProject.isFavourite;
                
                await this.singularUpdateProject({ payload: { isFavourite: val } });

                this.$toast.success(`${val ? "Added to" : "Removed from"} favourites`);
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.appState = "";
            }
        },

        async toggleEnableReminders(val) {
            try {
                this.upgradeActionType = "";

                if (["free", "standard"].includes(this.accountPlan)) {
                    const maxLimit = MAX_COUNT_BY_FEATURES[this.accountPlan].reminderCount;
    
                    if (typeof maxLimit === "undefined") {
                        this.upgradeActionType = "project_reminders_free";
                        this.modalActionType = "showUpgradeModalAlttt";
                        return;
                    }

                    if (this.projectRemindersCount >= maxLimit) {
                        this.upgradeActionType = "project_reminders_exhausted";
                        this.modalActionType = "showUpgradeModalAlttt";
                        return;
                    }
                }

                await this.singularUpdateProject({ payload: { enableReminder: val } });
                this.$toast.success(`Reminder ${val ? "enabled" : "disabled"}`);
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            }
        },

        async updateProjectTitle(val) {
            try {
                if (val === this.currentProject.title) return;

                debounce(async() => {
                    await this.singularUpdateProject({ payload: { title: val } });   
                }, 800, "project-title");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            }
        },

        async handleDeleteProject() {
            try {
                this.deleteState.loading = true;
                this.deleteState.err = false;
                this.deleteState.errMsg = "";

                const ids = [this.currentProject?._id];
                await this.$store.dispatch("project/deleteProjects", ids);

                this.$toast.success("Project deleted successfully");
                return this.$router.push({ path: "/dashboard/projects" });
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;

                this.deleteState.err = false;
                this.deleteState.errMsg = errMsg;
                this.$toast.error(errMsg);
            } finally {
                this.deleteState.loading = false;
            }
        },

        async handleArchiveProject() {
            try {
                const id = this.currentProject?._id || this.$route.params.id;

                if (!id) {
                    this.$toast.error("No project id passed");
                    return;
                }

                this.pageLoadingErr = false;
                this.pageLoadingErrMsg = "";
                this.pageLoading = true;

                await this.$store.dispatch("project/archiveProject", id);

                this.showArchiveModal = false;
                this.$toast.success("Project archived");
                this.$router.push("/dashboard/projects");
            } catch (error) {
                const msg = error?.message;

                this.pageLoadingErr = true;
                this.$toast.error(msg);
            } finally {
                this.pageLoading = false;
            }
        },

        openEditModal(val) {
            if (val === "trackingDetails") {
                if (["free", "standard"].includes(this.accountPlan)) {
                    const maxLimit = MAX_COUNT_BY_FEATURES[this.accountPlan].reminderCount;
    
                    if (typeof maxLimit === "undefined") {
                        this.upgradeActionType = "project_reminders_free";
                        this.modalActionType = "showUpgradeModalAlttt";
                        return;
                    }

                    if (this.projectRemindersCount >= maxLimit) {
                        this.upgradeActionType = "project_reminders_exhausted";
                        this.modalActionType = "showUpgradeModalAlttt";
                        return;
                    }
                }
            }

            this.editModalType = val;
            this.showEditModal = true;
        },
        
        toggleDelete() {
            if (this.modalActionType === "") {
                this.modalActionType = "showDeleteModal";
            } else {
                this.modalActionType = "";
            }
        },

        openRemindersModal() {
            if (["free", "standard"].includes(this.accountPlan)) {
                const maxLimit = MAX_COUNT_BY_FEATURES[this.accountPlan].reminderCount;

                if (typeof maxLimit === "undefined") {
                    this.upgradeActionType = "project_reminders_free";
                    this.modalActionType = "showUpgradeModalAlttt";
                    return;
                }

                if (this.projectRemindersCount >= maxLimit) {
                    this.upgradeActionType = "project_reminders_exhausted";
                    this.modalActionType = "showUpgradeModalAlttt";
                    return;
                }
            }

            if (!this.currentProject.enableReminder) {
                this.$toast.info("Enable reminders to use this feature");
                return;
            }

            if (!this.currentProject.deadline) {
                this.$toast.info("Set a deadline to add reminders");
                return;
            }

            this.showRemindersModal = true;
        },

        async duplicateProject() {
            try {
                const maxLimit = MAX_COUNT_BY_COLLECTIONS[this.accountPlan].projects?.create;

                if ( ["free", "standard"].includes(this.accountPlan)) {
                    if (typeof maxLimit === "undefined" || (maxLimit !== 0 && this.projectTotal >= maxLimit)) {
                        this.upgradeActionType = "projects";
                        this.modalActionType === "showUpgradeModalAlt";
                        return;
                    }
    
                    if (this.isSearched) {
                        await this.$store.dispatch("app/checkUpgradeStatus", "projects");
                    }
                }

                this.appState = "duplicate_project_loading";
                
                const res = await this.$store.dispatch("project/duplicateProject", this.currentProject._id);

                if (!res._id) {
                    this.$toast.error("Unable to duplicate project");        
                }

                if (res.status === "completed") {
                    await this.$store.dispatch("app/fetchCompletedProjectCount", { refresh: true, manualUpdate: true });

                    if (completedProjectMarkers.includes(this.completedProjectCount)) {
                        this.$store.dispatch("app/toggleConfetti", {
                            show: true,
                            type: "rank",
                        });
                    }
                }

                return this.$router.push(`/dashboard/projects/${res._id}`);
            } catch (error) {
                
            } finally {
                this.appState = "";
            }
        },

        buildSubPayload() {
            const payload = {
                _id: this.currentProject._id,
                title: this.currentProject.title,
                description: this.currentProject.description,
                status: this.currentProject.status?.toLowerCase(),
                deadline: this.currentProject.deadline,
                priority: this.currentProject.priority,
                projectType: this.currentProject.projectType?.toLowerCase(),
                estimatedRevenue: this.currentProject.estimatedRevenue,
                isFavourite: this.currentProject.isFavourite === "no" ? false : true,
                currency: this.currentProject.currency,
                billableHours: Number(this.currentProject.billableHours || ""),
                reminders: this.currentProject.reminders,
                trackProject: this.currentProject.trackProject === "Yes" ? true : false,
                trackingDays: this.currentProject.trackingDays,
                trackingType: this.currentProject.trackingType || "none",
            };

            return payload;
        },

        async singularUpdateProject({ payload, resetFn, externalPayload }) {              
            try {              
                const initialPayload = { ...this.buildSubPayload() };                
                const res = await this.$store.dispatch("project/updateProject", { 
                    id: this.currentProject._id, 
                    payload: { 
                        ...initialPayload, 
                        ...payload, 
                    }, 
                    externalPayload,
                });
                
                this.currentProject = {
                    ...this.currentProject,
                    ...res,
                };

                if (resetFn) {
                    resetFn();
                }
            } catch (error) {
                throw error;
            }
        },

        async handleUpdateProject({ payload, resetFn }) {              
            try {              
                this.modalSubmitBtn = "projectUpdate";

                await this.singularUpdateProject({ payload, resetFn });
                this.showEditModal = false;
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                if (!external) {
                    this.modalSubmitBtn = "";
                }
            }
        },

        async updatePriority(val) {
            try {
                this.appState = "update_priority_loading";
                
                const payload = { priority: val };
    
                if (val === this.currentProjectpriority) return;
    
                this.currentProjectpriority = val;
                await this.singularUpdateProject({ payload });
            } catch (error) {
                const errMsg =error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.appState = "";
            }
        },

        async manageClients() {
            try {
                this.modalSubmitBtn = "manageClients";

                const payload = { clientIds: this.form.clients.length ? this.form.clients.map(x => x._id) : [] };    
                await this.singularUpdateProject({ payload });
                
                this.currentProject.clients = this.form.clients;
                this.showClientsModal = false;
            } catch (error) {
                throw error;
            }  finally {
                this.modalSubmitBtn = "";
            }
        },

        async manageReminders(data) {
            try {
                this.modalSubmitBtn = "manageReminders";
                const payload = { reminders: data };
                
                await this.singularUpdateProject({ payload });

                this.reminders = data;
                this.showRemindersModal = false;
            } catch (error) {
                const errMsg =error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.modalSubmitBtn = "";
            }
        },

        resetClientSelection() {
            this.form.clients = this.currentProject.clients;
            this.showClientsModal = false;
        },

        resetRateCardSelection() {
            this.form.rateCard = this.currentProject.rateCard;
            this.modalActionType = "";
        },

        async searchClients(query) {
            try {
                this.searchItemsErr = false;
                this.searchItemsLoading = true;

                const pageQueryObj = {
                    page: 1,
                    type: "all",
                    name: query,
                };

                const buildResult = assembleQueryList(pageQueryObj);
                this.clients = await this.$store.dispatch("clients/fetchClients", { params: buildResult, refresh: true, external: true });
            } catch (error) {
                this.searchItemsErr = true;
            } finally {
                this.searchItemsLoading = false;
            }
        },

        async searchRateCards(query) {            
            try {                
                this.searchItemsErr = false;
                this.searchItemsLoading = true;

                const pageQueryObj = {
                    page: 1,
                    title: query,
                };

                const buildResult = assembleQueryList(pageQueryObj);
                this.rateCards = await this.$store.dispatch("ratecards/fetchRateCards", { payload: buildResult, refresh: true, external: true });
            } catch (error) {
                this.searchItemsErr = true;
            } finally {
                this.searchItemsLoading = false;
            }
        },

        duration(end, start) {
            const elapsedTime = (end || new Date().getTime()) - start || 0;
            const seconds = Math.floor((elapsedTime / 1000) % 60);
            const minutes = Math.floor((elapsedTime / (1000 * 60)) % 60);
            const hours = Math.floor(elapsedTime / (1000 * 60 * 60));
            
            return [hours, minutes, seconds]
                .map(n => n.toString().padStart(2, "0"))
                .join(":");
        },
        
        closeModal(){
            this.$emit("cancel");
        },

        hide(event) {
            event.preventDefault();
            event.stopPropagation();
        },

        async replacePreviousTracking() {            
            try {           
                this.modalSubmitBtn = "replacePreviousTracking";

                const projectId = this.$route.params.id || this.currentProject._id;

                if (!projectId) {
                    this.$toast.error("Something went wrong");
                    return;
                }
    
                const payload = {
                    projectData: {
                        startDate: new Date(),
                        status: "in progress",
                        trackingType: "automatic",
                    },
                    logData: {
                        eventType: "start",
                        startTime: Date.now(),
                        endTime: null,
                        projectId,
                    },
                };

                const resp = await this.$store.dispatch("project/startProject", { payload, id: projectId });

                if (resp?._id) {
                    this.currentProject = {
                        ...this.currentProject,
                        ...resp,
                    };
                }
                // use this since the "trackProject" flag isn't changed
                this.startTimer();
                this.$toast.success("Tracking started");
                this.modalActionType === "";
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.modalSubmitBtn = "";
            }
        },

        async handleTrackingChange(val, event) {            
            try {                
                const projectId = this.$route.params.id || this.currentProject._id;
    
                const payload = {
                    eventType: event,
                };

                if (event === "start" || val) {
                    payload.trackProject = true;
                    payload.startDate = Date.now();
                    payload.startTime = Date.now();
                    payload.endTime = null;
                }

                if (event === "stop" || !val) {
                    payload._id = this.logItem._id;
                    payload.trackProject = false;
                    payload.startTime = this.logItem.startTime;
                    
                    // set end time
                    if (this.currentProject.trackingType === "manual") {
                        payload.endTime = this.logItem?.endTime;
                    } else {
                        payload.endTime = Date.now();
                    }
                }

                const resp = await this.$store.dispatch("project/toggleLog", { projectId, payload });
                this.$toast.success(`${event === "start" ? "Tracking started" : "Tracking stopped"}`);
                
                await this.$store.dispatch("project/fetchLogs", { projectId });
                
                if (resp?.project?.trackProject === this.currentProject?.trackProject) {                    
                    // no need to update the current project and run the watcher function
                    if (event === "start" || val) {
                        this.startTimer();
                        return;
                    }

                    if (event === "stop" || !val) {
                        this.stopTimer();
                        return;
                    }
                } else {                    
                    this.currentProject = {
                        ...this.currentProject,
                        trackProject: resp?.project?.trackProject,
                    };
    
                    console.log({ "currentProject.trackProject block": this.currentProject.trackProject});
                }
            } catch (error) {                
                throw error;
            }
        },

        async toggleTracking() {
            try {                
                if (this.currentProject.trackingType === "scheduled" || this.currentProject.trackingType === "scheduled_tracker") {
                    this.modalActionType = "showStartWarningModal";
                    return;
                }

                // start tracking timer
                if (!this.isTracking) { // user hasn't started tracking project yet
                    if (["free", "standard"].includes(this.accountPlan)) {
                        const maxLimit = MAX_COUNT_BY_FEATURES[this.accountPlan].reminderCount;
        
                        if (typeof maxLimit === "undefined") {
                            this.upgradeActionType = "project_reminders_free";
                            this.modalActionType = "showUpgradeModalAlttt";
                            return;
                        }

                        if (this.projectRemindersCount >= maxLimit) {
                            this.upgradeActionType = "project_reminders_exhausted";
                            this.modalActionType = "showUpgradeModalAlttt";
                            return;
                        }
                    }

                    this.isTracking = true;
                    this.appState = "track_project_loading";

                    await this.handleTrackingChange(true, "start");
                }  else {
                    await this.handleTrackingChange(false, "stop");
                }
            } catch (error) {
                throw error;
            } finally {
                this.appState = "";
            }
        },

        startTimer() {              
            const current = this.activityLogs[0];

            if (current?.startTime && !current.endTime) {
                this.logItem.startTime = current.startTime;
            } else {
                this.logItem.startTime = Date.now() - this.elapsedTime;
            }

            this.timer = setInterval(this.updateTimer, 1000);
            // this.startIdleCheck();
        },

        async stopTimer() {
            clearInterval(this.timer);
            clearInterval(this.idleCheckTimer);

            this.elapsedTime = 0; 
        },

        updateTimer() {
            this.elapsedTime = Date.now() - this.logItem.startTime;
        },

        updateLastActive() {
            this.lastActive = Date.now();
        },

        startIdleCheck() {
            this.idleCheckTimer = setInterval(() => {
                const idleTime = Date.now() - this.lastActive;
                if (idleTime > 60000 && this.isTracking) { // 1 minute idle threshold
                    this.logActivity("idle", Math.floor(idleTime / 1000));
                }
            }, 30000);
        },

        // handleVisibilityChange() {
        //     this.logActivity(document.hidden ? "tab_inactive" : "tab_active");
        // },

        logActivity(type, duration = 0) {
            this.activityLog.push({
                timestamp: new Date().toISOString(),
                type,
                duration,
            });
        },

        setupEventListeners() {
            const events = ["mousemove", "keydown", "click", "scroll"];
            events.forEach(event => {
                window.addEventListener(event, this.updateLastActive);
            });
            
            document.addEventListener("visibilitychange", this.handleVisibilityChange);
        },

        removeEventListeners() {
            const events = ["mousemove", "keydown", "click", "scroll"];
            events.forEach(event => {
                window.removeEventListener(event, this.updateLastActive);
            });
            
            document.removeEventListener("visibilitychange", this.handleVisibilityChange);
        },

        toggleRevenue() {
            if (this.currentProject.projectType === "not paid") {
                this.$toast.info("Update the payment status and currency of this project");
                return;
            }

            this.openEditModal("estimatedRevenue");
        },

        async shareProject() {
            try {
                const id = this.currentProject?._id || this.$route.params.id;

                if (!id) {
                    this.$toast.error("Unable to proceed with request");
                    return;
                }

                this.modalSubmitBtn = "shareProject";

                await this.$store.dispatch("project/archiveProject", id);
            } catch (error) {
                const msg = error?.message;

                this.pageLoadingErr = true;
                this.$toast.error(msg);
            } finally {
                this.modalSubmitBtn = "";
            }
        },
    },
};
</script>